import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { inventoryAPI } from '../../services/api';
import { FiFileText, FiAlertCircle } from 'react-icons/fi';
import { LuInfo, LuPlus, LuEye, LuPencil, LuSearch } from 'react-icons/lu';
import api from '../../services/api';

// Components
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorAlert from '../../components/ErrorAlert';
import SuccessAlert from '../../components/SuccessAlert';
import CustomerInventoryForm from '../../components/forms/CustomerInventoryForm';
import ListPaginationControls from '../../components/ListPaginationControls';

const formatDisplayValue = (value, fallback = '') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .map((entry) => formatDisplayValue(entry, ''))
      .filter(Boolean)
      .join(', ') || fallback;
  }

  if (typeof value === 'object') {
    return value.name || value.label || value.code || fallback;
  }

  return fallback;
};

const Inventory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    location: ''
  });


  const queryClient = useQueryClient();
  
  // Fetch inventory data for the current customer
  const { data, isLoading, error, refetch, isPreviousData } = useQuery(
    ['customer-inventory', searchTerm, page, limit, filters.status],
    () => inventoryAPI.getInventory({
      page,
      limit,
      search: searchTerm
    }),
    { 
      keepPreviousData: true,
      staleTime: 30000, // 30 seconds
      onError: (error) => {
        console.error('Error fetching inventory:', error);
      }
    }
  );

  // Reset to first page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, filters.status, limit]);
  


  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to first page when search changes
  };



  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Filter inventory items based on available quantity only
  const filteredItems = data?.data?.filter(item => {
    if (!item) return false;
    
    const availableQuantity = Number(item.quantity) || 0;
    
    return filters.status === 'all' || 
           (filters.status === 'low-stock' && item.reorderThreshold && availableQuantity > 0 && availableQuantity <= item.reorderThreshold) ||
           (filters.status === 'in-stock' && availableQuantity > 0 && (!item.reorderThreshold || availableQuantity > item.reorderThreshold)) ||
           (filters.status === 'out-of-stock' && availableQuantity <= 0);
  }) || [];


  
  // Open note modal for an item
  const openNoteModal = (item) => {
    setSelectedItem(item);
    setNoteText(item.notes || '');
    setShowNoteModal(true);
  };
  
  // Close note modal
  const closeNoteModal = () => {
    setShowNoteModal(false);
    setSelectedItem(null);
    setNoteText('');
  };
  
  if (isLoading && !data) {
    return (
      <div className="min-h-[60vh] bg-gray-50 flex items-start justify-center pt-24">
        <LoadingSpinner />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="py-6 px-4">
        <ErrorAlert 
          message="Error loading inventory" 
          details={error.message}
          onRetry={() => refetch()}
        />
      </div>
    );
  }
  
  // Empty state if no inventory
  if (!data?.data || data.data.length === 0) {
    return (
      <div className="py-6 px-4">
        <div className="sm:flex sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Inventory Management</h1>
            <p className="mt-2 text-sm text-gray-700">
              View and manage your inventory items and stock levels.
            </p>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <LuPlus className="-ml-1 mr-2 h-5 w-5" />
              Add Item
            </button>
          </div>
        </div>

        <div className="py-10 px-4 text-center">
          <LuInfo className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">
            No Inventory Items
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm 
              ? `No results match "${searchTerm}". Try a different search term.` 
              : "You don't have any inventory items in the system yet."}
          </p>
          <div className="mt-6">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <LuPlus className="-ml-1 mr-2 h-5 w-5" />
              Add Inventory Item
            </button>
          </div>
        </div>

        {showCreateModal && (
          <CustomerInventoryForm
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setSuccessMessage('Product created successfully!');
              setTimeout(() => setSuccessMessage(''), 5000);
            }}
          />
        )}
      </div>
    );
  }
  
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-4">
          <SuccessAlert message={successMessage} />
        </div>
      )}
      {errorMessage && (
        <div className="mb-4">
          <ErrorAlert message={errorMessage} />
        </div>
      )}

      <nav className="mb-4 text-sm text-gray-500" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2">
          <li>Inventory Management</li>
          <li>/</li>
          <li className="font-medium text-gray-700">Entries</li>
        </ol>
      </nav>

      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Inventory Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            View and manage your inventory items and stock levels.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
              <LuPlus className="-ml-1 mr-2 h-5 w-5" />
              Add Item
            </button>
          </div>
      </div>
      
      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 sm:p-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="col-span-1">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">Search Items</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LuSearch className="h-4 w-4 text-gray-500" />
              </div>
              <input
                id="search"
                type="text"
                placeholder="Search by SKU, name, serial..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>
          <div>
            <label htmlFor="stockFilter" className="block text-sm font-medium text-gray-700 mb-1">Available Quantity</label>
            <select
              id="stockFilter"
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="all">All Items</option>
              <option value="in-stock">Available</option>
              <option value="low-stock">Low Available</option>
              <option value="out-of-stock">No Available Stock</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              This filter uses currently available units only. Pending receive, pending shipment, and held units are tracked separately on item detail.
            </p>
          </div>
        </div>
      </div>
      
      {/* Inventory table */}
      <div className="bg-white shadow overflow-hidden rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SKU / Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Available
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pending Receive
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pending Shipment
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(isLoading && !isPreviousData) ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : filteredItems?.length > 0 ? (
                filteredItems.map((item) => {
                  const availableQuantity = Number(item.quantity) || 0;
                  const pendingReceive = Number(item.pendingReceive) || 0;
                  const pendingShipment = Number(item.pendingShipment) || 0;
                  return (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              <Link
                                to={`/customer/inventory/${item.id}`}
                                className="text-indigo-600 hover:text-indigo-900 hover:underline"
                              >
                                {formatDisplayValue(item.productSku, 'No SKU')}
                              </Link>
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatDisplayValue(item.productName, 'Unnamed item')}
                            </div>
                            {item.serialNumber && (
                              <div className="text-xs text-gray-400">
                                SN: {formatDisplayValue(item.serialNumber)}
                              </div>
                            )}
                            {item.vendor && (
                              <div className="text-xs text-gray-400">
                                Vendor: {formatDisplayValue(item.vendor)}
                              </div>
                            )}
                            {item.binLocation && (
                              <div className="text-xs text-gray-400">
                                Location: {formatDisplayValue(item.binLocation)}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{availableQuantity}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{pendingReceive}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{pendingShipment}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-3">
                          <Link
                            to={`/customer/inventory/${item.id}`}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <LuEye className="h-4 w-4" />
                            <span className="sr-only">View details</span>
                          </Link>
                          <Link
                            to={`/customer/inventory/${item.id}/edit`}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <LuPencil className="h-4 w-4" />
                            <span className="sr-only">Edit</span>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    {searchTerm || filters.status !== 'all' 
                      ? 'No inventory items found matching your criteria.' 
                      : 'No inventory items found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {data && (
        <ListPaginationControls
          currentPage={page}
          totalPages={data.totalPages || 1}
          totalItems={data.total || filteredItems.length}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={(nextLimit) => {
            setLimit(nextLimit);
            setPage(1);
          }}
        />
      )}
      
      {/* Note Modal */}
      {showNoteModal && selectedItem && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                {formatDisplayValue(selectedItem.productName, 'Inventory Item')} ({formatDisplayValue(selectedItem.productSku, 'No SKU')})
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Item details and notes
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Details</h4>
                  <dl className="mt-2 divide-y divide-gray-200">
                    <div className="flex justify-between py-2">
                      <dt className="text-sm font-medium text-gray-500">Quantity:</dt>
                      <dd className="text-sm text-gray-900">{selectedItem.quantity}</dd>
                    </div>
                    <div className="flex justify-between py-2">
                      <dt className="text-sm font-medium text-gray-500">Location:</dt>
                      <dd className="text-sm text-gray-900">{formatDisplayValue(selectedItem.binLocation, 'Not assigned')}</dd>
                    </div>
                    {selectedItem.serialNumber && (
                      <div className="flex justify-between py-2">
                        <dt className="text-sm font-medium text-gray-500">Serial Number:</dt>
                        <dd className="text-sm text-gray-900">{formatDisplayValue(selectedItem.serialNumber)}</dd>
                      </div>
                    )}
                    {selectedItem.macAddress && (
                      <div className="flex justify-between py-2">
                        <dt className="text-sm font-medium text-gray-500">MAC Address:</dt>
                        <dd className="text-sm text-gray-900">{formatDisplayValue(selectedItem.macAddress)}</dd>
                      </div>
                    )}
                  </dl>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Notes</h4>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded border border-gray-200 min-h-[80px]">
                      {selectedItem.notes || 'No notes available for this item.'}
                    </p>
                  </div>
                </div>
                <div className="pt-4 text-right">
                  <button
                    type="button"
                    onClick={closeNoteModal}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Inventory Item Form */}
      {showCreateModal && (
        <CustomerInventoryForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setSuccessMessage('Product created successfully!');
            setTimeout(() => setSuccessMessage(''), 5000);
          }}
        />
      )}
    </div>
  );
};

export default Inventory; 
